import React, { useEffect, useState } from 'react';

import {
    Alert,
    Button,
    Checkbox,
    Card,
    Descriptions,
    Select,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';

import type { ColumnsType } from 'antd/es/table';

import moment from 'moment';

import { apiSubscriptionAPI } from '../../http/ApiSubscriptionAPI';
import {
    ApiInvoiceItem,
    ApiPaymentItem,
    ApiSubscriptionResponse,
    ApiSubscriptionTariff,
} from '../../models/ApiSubscription';

const { Title, Paragraph, Text } = Typography;

type SubState = 'none' | 'pending' | 'active' | 'expired';

interface Computed {
    state: SubState;
    daysLeft: number;
    periodStart: string | null;
    periodEnd: string | null;
}

const computeState = (data: ApiSubscriptionResponse): Computed => {
    const sub = data.subscription;
    if (!data.has_subscription || !sub) {
        return { state: 'none', daysLeft: 0, periodStart: null, periodEnd: null };
    }
    const start = sub.period_start;
    const end = sub.period_end;
    if (sub.status === 'pending') {
        return { state: 'pending', daysLeft: 0, periodStart: start, periodEnd: end };
    }
    if (sub.status === 'active' && end) {
        const ms = new Date(end).getTime() - Date.now();
        if (ms > 0) {
            const days = Math.ceil(ms / (24 * 3600 * 1000));
            return { state: 'active', daysLeft: days, periodStart: start, periodEnd: end };
        }
    }
    return { state: 'expired', daysLeft: 0, periodStart: start, periodEnd: end };
};

const statusTag = (state: SubState, daysLeft: number) => {
    switch (state) {
        case 'active':
            return (
                <Tag color={daysLeft <= 7 ? 'orange' : 'green'}>
                    {daysLeft <= 7
                        ? `Истекает через ${daysLeft} дн.`
                        : `Активна (осталось ${daysLeft} дн.)`}
                </Tag>
            );
        case 'pending':
            return <Tag color="blue">Ожидает оплаты</Tag>;
        case 'expired':
            return <Tag color="red">Истекла</Tag>;
        default:
            return <Tag>Не оформлена</Tag>;
    }
};

const paymentStatusTag = (status: string) => {
    switch (status) {
        case 'succeeded':
            return <Tag color="green">Оплачен</Tag>;
        case 'pending':
        case 'waiting_for_capture':
            return <Tag color="blue">Ожидает оплаты</Tag>;
        case 'canceled':
            return <Tag color="red">Отменён</Tag>;
        default:
            return <Tag>{status}</Tag>;
    }
};

const TariffCard: React.FC<{ tariff: ApiSubscriptionTariff }> = ({ tariff }) => (
    <Descriptions column={1} size="small" style={{ marginTop: 12 }}>
        <Descriptions.Item label="Название">{tariff.name}</Descriptions.Item>
        <Descriptions.Item label="Описание">{tariff.description}</Descriptions.Item>

    </Descriptions>
);

const ApiSubscriptionPanel: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [acceptOfferApi, setAcceptOfferApi] = useState(false);
    const [data, setData] = useState<ApiSubscriptionResponse | null>(null);
    const [selectedMonths, setSelectedMonths] = useState<number>(1);
    const [payments, setPayments] = useState<ApiPaymentItem[]>([]);
    const [invoices, setInvoices] = useState<ApiInvoiceItem[]>([]);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'card' | 'invoice'>('card');

    const load = () => {
        setLoading(true);
        Promise.all([
            apiSubscriptionAPI.Get(),
            apiSubscriptionAPI.GetPayments().catch(() => ({ payments: [] })),
            apiSubscriptionAPI.ListInvoices().catch(() => ({ invoices: [] })),
        ])
            .then(([subRes, payRes, invRes]) => {
                setData(subRes);
                setPayments(payRes.payments || []);
                setInvoices(invRes.invoices || []);
                // Если выбранного периода нет в списке — берём первый доступный.
                const list = subRes.periods || [];
                if (list.length > 0 && !list.some((p) => p.months === selectedMonths)) {
                    setSelectedMonths(list[0].months);
                }
            })
            .catch((err) => {
                const msg =
                    err?.response?.data?.message || 'Не удалось загрузить данные подписки';
                message.error(msg);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const handlePay = () => {
        const needAccept = !data?.api_offer_version;
        if (needAccept && !acceptOfferApi) {
            message.error('Примите условия оферты API для продолжения оплаты');
            return;
        }
        setPaying(true);
        apiSubscriptionAPI
            .Pay(needAccept, selectedMonths)
            .then((res) => {
                if (res.confirmation_url) {
                    window.location.href = res.confirmation_url;
                } else {
                    message.error('Не получен адрес для оплаты');
                    setPaying(false);
                }
            })
            .catch((err) => {
                const msg = err?.response?.data?.message || 'Не удалось создать платёж';
                message.error(msg);
                setPaying(false);
            });
    };

    // Скрываем pending-платежи старше 15 минут
    const activePayments = payments.filter((p) => {
        if (p.status === 'pending') {
            const ageMs = Date.now() - new Date(p.created_at).getTime();
            return ageMs < 15 * 60 * 1000;
        }
        return true;
    });

    const columns: ColumnsType<ApiPaymentItem> = [
        {
            title: 'Дата',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (v: string) => moment(v).format('DD.MM.YYYY HH:mm'),
        },
        {
            title: 'Сумма',
            dataIndex: 'amount',
            key: 'amount',
            render: (v: number) => `${v.toLocaleString('ru-RU')} ₽`,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (v: string) => paymentStatusTag(v),
        },
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!data) {
        return (
            <Alert
                type="error"
                message="Не удалось загрузить данные подписки"
                action={
                    <Button size="small" onClick={load}>
                        Повторить
                    </Button>
                }
            />
        );
    }

    const c = computeState(data);
    const canPay = c.state === 'none' || c.state === 'expired' || c.state === 'pending' || c.state === 'active';
    const buttonText = c.state === 'pending' ? 'Оплатить снова' : c.state === 'active' ? 'Продлить' : 'Оплатить';
    // Чекбокс оферты API — только при первой оплате.
    const needAcceptOffer = !data?.api_offer_version;
    const selectedPeriod = data?.periods?.find((p) => p.months === selectedMonths);

    const handleCreateInvoice = () => {
        if (needAcceptOffer && !acceptOfferApi) {
            message.error('Примите условия оферты API для выставления счёта');
            return;
        }
        setCreatingInvoice(true);
        apiSubscriptionAPI
            .CreateInvoice(selectedMonths)
            .then((res) => {
                message.success(`Счёт ${res.number} сформирован`);
                return apiSubscriptionAPI.ListInvoices();
            })
            .then((res) => setInvoices(res.invoices || []))
            .catch((e) => {
                const msg = e?.response?.data?.message || 'Не удалось сформировать счёт';
                message.error(msg);
            })
            .finally(() => setCreatingInvoice(false));
    };

    const handleDownloadInvoice = (r: ApiInvoiceItem) => {
        apiSubscriptionAPI
            .DownloadInvoice(r.id)
            .then((blob) => {
                const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `${r.number}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => message.error('Не удалось скачать счёт'));
    };

    const invoiceColumns: ColumnsType<ApiInvoiceItem> = [
        {
            title: 'Номер',
            dataIndex: 'number',
            key: 'number',
        },
        {
            title: 'Период',
            dataIndex: 'months',
            key: 'months',
            render: (v: number) => `${v} мес.`,
        },
        {
            title: 'Сумма',
            dataIndex: 'amount_total',
            key: 'amount_total',
            render: (v: number) => `${v.toFixed(2)} ₽`,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (v: string) => {
                switch (v) {
                    case 'paid':
                        return <Tag color="green">Оплачен</Tag>;
                    case 'issued':
                        return <Tag color="blue">Ожидает оплаты</Tag>;
                    case 'expired':
                        return <Tag color="red">Истёк</Tag>;
                    case 'canceled':
                        return <Tag>Отменён</Tag>;
                    default:
                        return <Tag>{v}</Tag>;
                }
            },
        },
        {
            title: 'Выставлен',
            dataIndex: 'issued_at',
            key: 'issued_at',
            render: (v: string) => moment(v).format('DD.MM.YYYY'),
        },
        {
            title: 'PDF',
            key: 'pdf',
            render: (_: unknown, r: ApiInvoiceItem) => (
                <Button size="small" onClick={() => handleDownloadInvoice(r)}>
                    Скачать
                </Button>
            ),
        },
    ];

    return (
        <>
            <Card
                title={
                    <span>
                        Подписка на API&nbsp;&nbsp;{statusTag(c.state, c.daysLeft)}
                    </span>
                }
                style={{ maxWidth: 720 }}
            >
                {c.state === 'active' && (
                    <Paragraph type="secondary">
                        Доступ активен до{' '}
                        <Text strong>{moment(c.periodEnd).format('DD.MM.YYYY')}</Text>.
                        {c.daysLeft <= 7 && (
                            <>
                                <br />
                                <Text type="warning">
                                    Подписка скоро истечёт — продлите её на выбранный период ниже.
                                </Text>
                            </>
                        )}
                    </Paragraph>
                )}

                {c.state === 'expired' && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Подписка истекла"
                        description={
                            c.periodEnd
                                ? `Срок действия закончился ${moment(c.periodEnd).format('DD.MM.YYYY')}. Оформите новую подписку для продолжения доступа.`
                                : 'Оформите новую подписку для продолжения доступа.'
                        }
                    />
                )}

                {c.state === 'pending' && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Платёж ещё не завершён"
                        description="Если вы уже оплатили — обновите страницу через минуту. Иначе нажмите «Оплатить снова»."
                    />
                )}

                <Title level={5} style={{ marginTop: 8 }}>
                    Тариф
                </Title>
                <TariffCard tariff={data.tariff} />

                {data.periods && data.periods.length > 0 && (
                    <>
                        <div className="api-pay-row">
                            <div className="api-pay-field">
                                <div className="api-pay-label">Период оплаты</div>
                                <Select
                                    value={selectedMonths}
                                    onChange={setSelectedMonths}
                                    style={{ width: '100%' }}
                                    size="large"
                                    options={data.periods.map((p) => ({
                                        value: p.months,
                                        label: `${p.months} мес.`,
                                    }))}
                                />
                            </div>
                            <div className="api-pay-total">
                                <div className="api-pay-label">К оплате</div>
                                <div className="api-pay-amount">
                                    {selectedPeriod ? selectedPeriod.price_rub.toLocaleString('ru-RU') : '—'} ₽
                                    {selectedPeriod && selectedPeriod.discount_pct > 0 && (
                                        <span style={{ fontSize: 14, color: '#417505', fontWeight: 500, marginLeft: 8 }}>
                                            (−{selectedPeriod.discount_pct}%)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {canPay && needAcceptOffer && (
                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                        <Checkbox checked={acceptOfferApi} onChange={(e) => setAcceptOfferApi(e.target.checked)}>
                            Принимаю условия{' '}
                            <a
                                href="https://agropogoda.com/static/files/api_license.pdf"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Лицензионного договора-оферты на использование API
                            </a>
                        </Checkbox>
                    </div>
                )}

                <div style={{ marginTop: 16, marginBottom: 8 }}>
                    <Text strong style={{ marginRight: 8 }}>Способ оплаты:</Text>
                    <Select
                        value={paymentMethod}
                        onChange={(v) => setPaymentMethod(v as 'card' | 'invoice')}
                        style={{ width: 260 }}
                        options={[
                            { value: 'card', label: 'Банковская карта (ЮKassa)' },
                            { value: 'invoice', label: 'Счёт на оплату (банковский перевод)' },
                        ]}
                    />
                </div>

                {paymentMethod === 'card' && (
                    <div className="api-pay-actions">
                        <Button
                            type="primary"
                            size="large"
                            disabled={!canPay || (needAcceptOffer && !acceptOfferApi)}
                            loading={paying}
                            onClick={handlePay}
                        >
                            {buttonText}
                        </Button>
                    </div>
                )}

                {paymentMethod === 'invoice' && (
                    <>
                        <Paragraph type="secondary">
                            Счёт для оплаты по реквизитам. После поступления денег подписка активируется.
                        </Paragraph>
                        <Button
                            type="primary"
                            loading={creatingInvoice}
                            disabled={!canPay}
                            onClick={handleCreateInvoice}
                        >
                            Сформировать счёт за {selectedMonths} мес.
                        </Button>
                    </>
                )}
            </Card>

            {paymentMethod === 'card' && (
                <Card title="История платежей" style={{ maxWidth: 720, marginTop: 16 }}>
                    <Table
                        rowKey="id"
                        dataSource={activePayments}
                        columns={columns}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: 'Платежей пока нет' }}
                    />
                </Card>
            )}

            {paymentMethod === 'invoice' && (
                <Card title="Счета" style={{ maxWidth: 720, marginTop: 16 }}>
                    <Table
                        rowKey="id"
                        dataSource={invoices}
                        pagination={false}
                        size="small"
                        locale={{ emptyText: 'Счетов пока нет' }}
                        columns={invoiceColumns}
                    />
                </Card>
            )}
        </>
    );
};

export default ApiSubscriptionPanel;
